<template>
  <div class="fwsed-firm-detail">
    <div class="d-flex align-items-center mb-3">
      <label class="mb-0 mr-2">{{ $t("firm.audit.dashboard.firm-select") }}</label>

      <filter-select
        id="parent-filter"
        ref="parent-filter"
        :placeholder="$t('firm.text')"
        :multiple="false"
        @filter="onFirmSearch"
        :selected="selectedFirm"
        @changed="firmSelect"
        track="_id"
      />
    </div>
    <div v-if="selectedFirm && auditOptions.length" class="d-flex align-items-center mb-3">
      <label class="mb-0 mr-2">{{ $t("firm.audit.dashboard.audit-select") || "Denetim" }}</label>
      <b-form-select v-model="selectedAuditId" :options="auditOptions" :disabled="isAuditsLoading" class="w-auto" />
    </div>

    <div v-if="isSummaryLoading && !firmOptions.length">
      <content-loading />
    </div>
    <div v-else-if="!firmOptions.length">
      <empty>
        <h5 class="text-center">{{ $t("firm.audit.dashboard.no-data") }}</h5>
      </empty>
    </div>
    <div v-else>
      <div v-if="!selectedFirm">
        <empty>
          <h5 class="text-center">{{ $t("firm.audit.dashboard.no-data") }}</h5>
        </empty>
      </div>
      <div v-else>
        <content-loading v-if="isFirmDetailLoading" />
        <div v-else>
          <empty v-if="!firmDetail">
            <h5 class="text-center">{{ $t("firm.audit.dashboard.no-data") }}</h5>
          </empty>
          <div v-else>
            <div class="firm-status mb-4">
              <span class="status-label">{{ $t("firm.audit.status.text") }}:</span>
              <b-badge :variant="statusVariant(firmStatus)">{{ formatStatus(firmStatus) }}</b-badge>
            </div>

            <div class="metric-section mb-4">
              <h6 class="section-title">{{ $t("firm.audit.dashboard.firm-detail.sections.questions") }}</h6>
              <div class="metric-grid">
                <div class="metric-card" v-for="card in questionCards" :key="card.label">
                  <div class="metric-label">{{ card.label }}</div>
                  <div class="metric-value">{{ card.value }}</div>
                </div>
              </div>
            </div>

            <div class="metric-section mb-4">
              <h6 class="section-title">{{ $t("firm.audit.dashboard.firm-detail.sections.dates") }}</h6>
              <div class="metric-grid">
                <div class="metric-card" v-for="card in dateCards" :key="card.label">
                  <div class="metric-label">{{ card.label }}</div>
                  <div class="metric-value">{{ card.value }}</div>
                </div>
              </div>
            </div>

            <div class="metric-section">
              <h6 class="section-title">{{ $t("firm.audit.dashboard.firm-detail.sections.durations") }}</h6>
              <div class="metric-grid">
                <div class="metric-card" v-for="card in durationCards" :key="card.label">
                  <div class="metric-label">{{ card.label }}</div>
                  <div class="metric-value">{{ card.value }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFirmStatus } from "@/helpers/filters";
import ContentLoading from "@c/content-loading.vue";
import Empty from "@c/empty.vue";

export default {
  name: "FwsedFirmDetail",
  components: { ContentLoading, Empty },
  data() {
    return {
      summaryRequest: {
        pagination: {
          options: { page: 1, limit: 200, sort: { createdAt: "desc" } },
        },
      },
    };
  },
  computed: {
    summaryPagination() {
      return this.$store.getters["firmAudit/summaryPagination"];
    },
    isSummaryLoading() {
      return this.$store.getters["firmAudit/isSummaryLoading"];
    },
    selectedFirm: {
      get() {
        return this.$store.getters["organizationFirm/selectedEntity"];
      },
      set(value) {
        this.$store.commit("organizationFirm/selectedEntity", value);
      },
    },
    auditsPagination() {
      return this.$store.getters["firmAudit/audits"];
    },
    audits() {
      return this.auditsPagination?.docs ?? [];
    },
    isAuditsLoading() {
      return this.$store.getters["firmAudit/isAuditsLoading"];
    },
    selectedAudit: {
      get() {
        return this.$store.getters["firmAudit/selectedEntity"];
      },
      set(value) {
        this.$store.commit("firmAudit/selectedEntity", value);
      },
    },
    selectedAuditId: {
      get() {
        return this.selectedAudit?._id;
      },
      set(value) {
        const audit = this.audits.find((a) => a._id === value);
        this.selectedAudit = audit;
      },
    },
    auditOptions() {
      return this.audits.map((audit) => ({
        value: audit._id,
        text: this.$options.filters.translate(audit.name),
      }));
    },
    summaryDocs() {
      return this.summaryPagination?.docs ?? [];
    },
    firmOptions() {
      return this.summaryDocs.map((doc) => ({
        value: doc.firm?._id,
        text: this.$options.filters.translate(doc.firm?.name),
      }));
    },
    firmDetail() {
      return this.$store.getters["firmAudit/firmDashboardDetail"];
    },
    isFirmDetailLoading() {
      return this.$store.getters["firmAudit/isFirmDashboardDetailLoading"];
    },
    currentProduct() {
      return this.$store.getters["product/currentProduct"] || "FWSED";
    },
    firmStatus() {
      return getFirmStatus(this.firmDetail?.firm, this.currentProduct);
    },
    questionCards() {
      const metrics = this.firmDetail?.metrics ?? {};
      return [
        { label: this.$t("firm.audit.dashboard.firm-detail.approved-questions"), value: metrics.approvedQuestions ?? 0 },
        { label: this.$t("firm.audit.dashboard.firm-detail.pending-questions"), value: metrics.pendingQuestions ?? 0 },
        { label: this.$t("firm.audit.dashboard.firm-detail.not-completed-questions"), value: metrics.notCompletedQuestions ?? 0 },
      ];
    },
    dateCards() {
      const dates = this.firmDetail?.dates ?? {};
      return [
        { label: this.$t("firm.audit.dashboard.firm-detail.audit-start-date"), value: this.formatDate(dates.auditStartedAt) },
        { label: this.$t("firm.audit.dashboard.firm-detail.test-approval-date"), value: this.formatDate(dates.testReadyAt) },
        { label: this.$t("firm.audit.dashboard.firm-detail.prod-approval-date"), value: this.formatDate(dates.prodReadyAt) },
      ];
    },
    durationCards() {
      const durations = this.firmDetail?.durations ?? {};
      return [
        { label: this.$t("firm.audit.dashboard.firm-detail.test-approval-duration"), value: this.$options.filters.duration(durations.testApproval) },
        { label: this.$t("firm.audit.dashboard.firm-detail.prod-approval-duration"), value: this.$options.filters.duration(durations.prodApproval) },
        // { label: this.$t("firm.audit.dashboard.firm-detail.audit-duration"), value: this.$options.filters.duration(durations.auditElapsed) },
      ];
    },
  },
  watch: {
    summaryRequest: {
      deep: true,
      immediate: true,
      handler() {
        this.init();
      },
    },
    summaryDocs: {
      immediate: true,
      handler(docs) {
        if (!docs || !docs.length) {
          this.selectedFirm = null;
          return;
        }
        const currentExists = docs.find((doc) => doc.firm?._id === this.selectedFirm?._id);
        if (!currentExists) {
          this.selectedFirm = docs[0].firm;
        }
      },
    },
    selectedFirm: {
      immediate: true,
      handler(newValue, oldValue) {
        if (newValue && newValue?._id !== oldValue?._id) {
          this.loadAudits();
          this.loadFirmDetail();
        }
        if (!newValue) {
          this.$store.commit("firmAudit/firmDashboardDetail", undefined);
        }
      },
    },
    audits(newVal) {
      if (newVal && newVal.length && !this.selectedAuditId) {
        this.selectedAudit = newVal[0];
      }
    },
    selectedAuditId(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadFirmDetail();
      }
    },
  },

  methods: {
    async onFirmSearch(value, callback) {
      const response = await this.$store.dispatch(`organizationFirm/paginate`, {
        options: { limit: 200 },
        query: { name: { value } },
      });
      callback(response.data?.docs ?? []);
    },
    firmSelect(item) {
      this.selectedFirm = item;
    },
    async init() {
      await this.loadFirmList();
    },
    async refresh() {
      const previousFirm = this.selectedFirm;
      await this.loadFirmList(true);
      if (this.selectedFirm && this.selectedFirm?._id === previousFirm?._id) {
        await this.loadFirmDetail();
      }
    },
    async loadFirmList(force = false) {
      if (!force && this.summaryDocs.length && !this.isSummaryLoading) {
        return;
      }
      try {
        await this.$store.dispatch("firmAudit/fetchSummary", this.summaryRequest);
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async loadAudits() {
      if (!this.selectedFirm?._id) {
        this.$store.commit("firmAudit/audits", undefined);
        this.selectedAudit = undefined;
        return;
      }
      try {
        await this.$store.dispatch("firmAudit/fetchAudits", {
          firmId: this.selectedFirm._id,
          pagination: { options: { page: 1, limit: 50, sort: { createdAt: "desc" } } },
        });
        if (!this.audits || !this.audits.length) {
          this.selectedAudit = undefined;
          return;
        }
        if (!this.selectedAudit && this.audits && this.audits.length) {
          this.selectedAudit = this.audits[0];
        }
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    async loadFirmDetail() {
      if (!this.selectedFirm) {
        return;
      }
      try {
        await this.$store.dispatch("firmAudit/fetchFirmDashboardDetail", { firmId: this.selectedFirm._id, auditId: this.selectedAuditId });
      } catch (error) {
        this.$handleHttpErrorByStatusCode(error);
      }
    },
    formatStatus(status) {
      if (!status) {
        return "-";
      }
      const questionSetMap = {
        FIRM_REVIEW: this.$t("firm.audit.status.firm-review"),
        BT_REVIEW: this.$t("firm.audit.status.bt-review"),
        COMPLETED: this.$t("firm.audit.status.completed"),
        IN_PROGRESS: this.$t("firm.audit.status.in-progress"),
        REJECTED: this.$t("firm.audit.status.rejected"),
        NONE: this.$t("firm.audit.status.none"),
      };
      const firmStatusMap = {
        REVIEW: this.$t("firm.status.review"),
        PRE_TEST: this.$t("firm.status.pre-test"),
        TEST: this.$t("firm.status.test"),
        PRE_PROD: this.$t("firm.status.pre-prod"),
        PROD: this.$t("firm.status.prod"),
        CANCELED: this.$t("firm.status.canceled"),
      };
      return firmStatusMap[status] || questionSetMap[status] || status;
    },
    statusVariant(status) {
      const questionSetVariantMap = {
        FIRM_REVIEW: "warning",
        BT_REVIEW: "info",
        COMPLETED: "success",
        IN_PROGRESS: "primary",
        REJECTED: "danger",
        NONE: "secondary",
      };
      const firmStatusVariantMap = {
        REVIEW: "secondary",
        PRE_TEST: "info",
        TEST: "warning",
        PRE_PROD: "primary",
        PROD: "success",
        CANCELED: "danger",
      };
      return firmStatusVariantMap[status] || questionSetVariantMap[status] || "secondary";
    },
    formatDate(value) {
      if (!value) {
        return "-";
      }
      return this.$options.filters.date ? this.$options.filters.date(value, true) : value;
    },
  },
};
</script>

<style scoped>
.metric-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 1rem;
}

.metric-card {
  background: #fff;
  border: 1px solid #e6ebf1;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.05);
}

.metric-label {
  font-size: 0.85rem;
  color: #6c757d;
}

.metric-value {
  font-size: 1.6rem;
  font-weight: 600;
  margin-top: 0.35rem;
}

.section-title {
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 1rem;
}

.firm-status .status-label {
  font-weight: 600;
  margin-right: 0.5rem;
}
</style>
